import React, {useContext, useEffect, useState, useCallback, useRef} from "react";
import {Link} from "react-router-dom";
import StyleContext from "../../contexts/StyleContext";
import {fetchBlogs, fetchCategories} from "../../services/api";
import {timeAgo} from "../../utils/timeAgo";
import {useLoading} from "../../contexts/LoadingContext";
import "./Blog.scss";

const PER_PAGE = 9;

// Three sortable fields. Each click toggles asc/desc on the active field;
// clicking a different field resets dir to "desc" (the natural "most/newest first").
const SORT_FIELDS = [
  {key: "created", label: "Date"},
  {key: "likes",   label: "Most Liked"},
  {key: "views",   label: "Most Viewed"},
];

function buildPageList(current, totalPages) {
  if (totalPages <= 7) return Array.from({length: totalPages}, (_, i) => i + 1);
  const pages = new Set([1, totalPages, current, current - 1, current + 1]);
  const sorted = [...pages].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("…");
  }
  return out;
}

const BlogList = () => {
  const {isDark} = useContext(StyleContext);
  const {show: showOverlay, hide: hideOverlay} = useLoading();

  const [blogs, setBlogs]     = useState([]);
  const [page, setPage]       = useState(1);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);

  const [view, setView] = useState(
    () => localStorage.getItem("blog_view") || "grid"
  );

  // ---- Sort: bidirectional toggle (default = newest first) ----
  const [sortBy,  setSortBy]  = useState("created");
  const [sortDir, setSortDir] = useState("desc");

  // ---- Category multi-select ----
  const [allCategories, setAllCategories]   = useState([]);
  const [selectedCats,  setSelectedCats]    = useState([]); // [] === "All Categories"
  const [catOpen,       setCatOpen]         = useState(false);
  const catBoxRef = useRef(null);

  // ---- Search (debounced, contextual to selectedCats) ----
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQ(searchInput.trim()), 280);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Load category list once.
  useEffect(() => {
    fetchCategories()
      .then(res => setAllCategories(res?.categories || []))
      .catch(() => setAllCategories([]));
  }, []);

  // Reset page whenever any filter changes.
  useEffect(() => { setPage(1); }, [sortBy, sortDir, q, selectedCats]);

  // Close the category dropdown on outside click / Esc.
  useEffect(() => {
    if (!catOpen) return;
    const onClick = e => {
      if (catBoxRef.current && !catBoxRef.current.contains(e.target)) setCatOpen(false);
    };
    const onKey = e => { if (e.key === "Escape") setCatOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [catOpen]);

  // ---- Lazy fetch — one page at a time ----
  const load = useCallback(async () => {
    setLoading(true);
    showOverlay("Loading posts");
    try {
      const data = await fetchBlogs({
        page,
        perPage: PER_PAGE,
        sortBy,
        dir: sortDir,
        q,
        categories: selectedCats,
      });
      setBlogs(data.blogs || []);
      setTotal(data.total || 0);
      setOffline(!!data.offline);
    } catch (e) {
      console.error("[BlogList] giving up", e);
      setBlogs([]); setTotal(0); setOffline(true);
    } finally {
      setLoading(false);
      hideOverlay();
    }
  }, [page, sortBy, sortDir, q, selectedCats, showOverlay, hideOverlay]);

  useEffect(() => { load(); }, [load]);

  // ---- Sort click handler: same field toggles, different field defaults DESC ----
  const onSortClick = key => {
    if (key === sortBy) {
      setSortDir(d => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(key);
      setSortDir("desc");
    }
  };

  // ---- Category multi-select handlers ----
  const toggleCat = name => {
    setSelectedCats(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };
  const clearCats = () => setSelectedCats([]);

  const setViewMode = mode => {
    setView(mode);
    localStorage.setItem("blog_view", mode);
  };

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pageList   = buildPageList(page, totalPages);

  const goTo = n => {
    if (n < 1 || n > totalPages || n === page) return;
    setPage(n);
    if (typeof window !== "undefined") window.scrollTo({top: 0, behavior: "smooth"});
  };

  const sortLabel = sortDir === "desc" ? "↓" : "↑";
  const catButtonLabel =
    selectedCats.length === 0
      ? "All Categories"
      : selectedCats.length === 1
        ? selectedCats[0]
        : `${selectedCats.length} categories`;

  return (
    <main className={`blog-page ${isDark ? "dark" : "light"}`}>
      <div className="blog-page__header">
        <div>
          <h1 className="blog-page__title">Blog</h1>
          <p className="blog-page__subtitle">
            Notes on Salesforce, Apex, LWC and full-stack development.
          </p>
        </div>
        <div className="blog-page__view-toggle" role="tablist">
          <button
            type="button"
            className={view === "grid" ? "active" : ""}
            onClick={() => setViewMode("grid")}
            aria-label="Grid view"
          >▦ Grid</button>
          <button
            type="button"
            className={view === "list" ? "active" : ""}
            onClick={() => setViewMode("list")}
            aria-label="List view"
          >☰ List</button>
        </div>
      </div>

      {/* ---------- Search + category picklist + bidirectional sort ---------- */}
      <div className="blog-page__controls">
        <div className="blog-page__search">
          <input
            type="search"
            className="rdf-search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder={
              selectedCats.length
                ? `Search inside ${selectedCats.length === 1 ? selectedCats[0] : selectedCats.length + " categories"}…`
                : "Search posts — matches title first, then content…"
            }
            aria-label="Search posts"
          />
          {searchInput && (
            <button
              type="button"
              className="blog-page__search-clear"
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
            >×</button>
          )}
        </div>

        {/* ---- Category picklist (multi-select dropdown) ---- */}
        <div className="blog-page__cat" ref={catBoxRef}>
          <button
            type="button"
            className={`blog-page__cat-btn ${selectedCats.length ? "has-value" : ""}`}
            onClick={() => setCatOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={catOpen}
          >
            <span className="blog-page__cat-btn-label">{catButtonLabel}</span>
            <span className="blog-page__cat-btn-caret" aria-hidden="true">▾</span>
          </button>

          {catOpen && (
            <div className="blog-page__cat-menu" role="listbox" aria-multiselectable="true">
              <button
                type="button"
                className={`blog-page__cat-option blog-page__cat-option--all ${selectedCats.length === 0 ? "active" : ""}`}
                onClick={() => { clearCats(); }}
              >
                <span>All Categories</span>
                {selectedCats.length === 0 && <span aria-hidden="true">✓</span>}
              </button>

              {allCategories.length === 0 ? (
                <div className="blog-page__cat-empty">No categories yet.</div>
              ) : allCategories.map(c => {
                const checked = selectedCats.includes(c.name);
                return (
                  <label key={c.name} className={`blog-page__cat-option ${checked ? "active" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCat(c.name)}
                    />
                    <span className="blog-page__cat-option-name">{c.name}</span>
                    <span className="blog-page__cat-option-count">{c.count}</span>
                  </label>
                );
              })}

              {selectedCats.length > 0 && (
                <button
                  type="button"
                  className="blog-page__cat-clear"
                  onClick={clearCats}
                >Clear selection</button>
              )}
            </div>
          )}
        </div>

        {/* ---- Bidirectional sort buttons ---- */}
        <div className="blog-page__filters" role="group" aria-label="Sort posts">
          {SORT_FIELDS.map(s => {
            const active = sortBy === s.key;
            return (
              <button
                key={s.key}
                type="button"
                className={`blog-page__filter ${active ? "active" : ""}`}
                onClick={() => onSortClick(s.key)}
                aria-pressed={active}
                title={active
                  ? `Sort by ${s.label} — ${sortDir === "desc" ? "highest first (click to flip)" : "lowest first (click to flip)"}`
                  : `Sort by ${s.label}`}
              >
                {s.label} {active && <span className="blog-page__filter-arrow">{sortLabel}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active filter chips */}
      {selectedCats.length > 0 && (
        <div className="blog-page__active-cats" aria-label="Active category filters">
          {selectedCats.map(c => (
            <span key={c} className="blog-page__active-cat">
              {c}
              <button
                type="button"
                onClick={() => toggleCat(c)}
                aria-label={`Remove ${c} filter`}
              >×</button>
            </span>
          ))}
          <button type="button" className="blog-page__active-cats-clear" onClick={clearCats}>
            Clear all
          </button>
        </div>
      )}

      {offline && (
        <div className="blog-page__notice">
          Showing offline content — live blog feed is temporarily unavailable.
        </div>
      )}

      {!loading && blogs.length === 0 ? (
        <div className="blog-page__state">
          {q
            ? `No posts match “${q}”${selectedCats.length ? ` in the selected ${selectedCats.length === 1 ? "category" : "categories"}` : ""}.`
            : selectedCats.length
              ? "No posts in the selected categories yet."
              : "No posts yet."}
        </div>
      ) : (
        <section className={`blog-list blog-list--${view}`}>
          {blogs.map(b => (
            <BlogCard key={b.id} blog={b} view={view} />
          ))}
        </section>
      )}

      {/* ---------- Pagination ---------- */}
      {totalPages > 1 && (
        <nav className="blog-page__pagination" aria-label="Pagination">
          <button
            type="button"
            onClick={() => goTo(page - 1)}
            disabled={page === 1 || loading}
            aria-label="Previous page"
          >‹ Prev</button>

          {pageList.map((n, i) =>
            n === "…" ? (
              <span key={`gap-${i}`} className="blog-page__pagination-gap" aria-hidden="true">…</span>
            ) : (
              <button
                key={n}
                type="button"
                onClick={() => goTo(n)}
                className={n === page ? "active" : ""}
                disabled={loading}
                aria-current={n === page ? "page" : undefined}
                aria-label={`Page ${n}`}
              >{n}</button>
            )
          )}

          <button
            type="button"
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages || loading}
            aria-label="Next page"
          >Next ›</button>
        </nav>
      )}

      {total > 0 && (
        <p className="blog-page__end">
          Page {page} of {totalPages} · {total} post{total === 1 ? "" : "s"}
        </p>
      )}
    </main>
  );
};

const BlogCard = ({blog, view}) => {
  const cover =
    blog.cover_image ||
    "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200";
  return (
    <article className={`blog-card blog-card--${view}`}>
      <Link to={`/blog/${blog.id}`} className="blog-card__link">
        <div
          className="blog-card__cover"
          style={{backgroundImage: `url(${cover})`}}
        />
        <div className="blog-card__body">
          {blog.category && <span className="blog-card__category">{blog.category}</span>}
          <h2 className="blog-card__title">{blog.title}</h2>
          <p className="blog-card__excerpt">{blog.excerpt}</p>
          <div className="blog-card__meta">
            <span>{blog.author || "Rashid Ahmad"}</span>
            <span>•</span>
            <span>{timeAgo(blog.created_at)}</span>
            <span>•</span>
            <span>{blog.views || 0} views</span>
            <span>•</span>
            <span>♥ {blog.likes || 0}</span>
          </div>
        </div>
      </Link>
    </article>
  );
};

export default BlogList;
